import {
  EntityState,
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit"
import Joi, { assert } from "joi"
import { get, isArray, isString, merge } from "lodash-es"

import { Client } from "shared/client"
import { ClientThingTypeMrn } from "shared/clientData"
import { Locker } from "shared/locker"
import { LockerThingTypeMrn } from "shared/lockerData"
import { LockerDoorReservationSession } from "shared/lockerDoorReservationSession"
import { LockerDoorReservationSessionThingTypeMrn } from "shared/lockerDoorReservationSessionData"
import { Reservation } from "shared/reservation"
import { ReservationThingTypeMrn } from "shared/reservationData"
import { SstDoor } from "shared/sst"
import { Thing } from "shared/thing"
import { type RootState } from "../../app/store"
import { SST2_TERMINAL_STATUSES, isSst2ReservationVisible } from "../../sst2Status"
import {
  collectTerminalReservationIds,
  getSst2ReservationId,
  isSst2TerminalStatus,
  reconcileLockerDoors,
  shouldKeepTerminalReservation,
} from "../../sst2Reconcile"
import { mblsconnectorApi } from "../mblsConnector/mblsConenctorApi"
import { MblsConnectorClient } from "../mblsConnector/mblsConnector"
import { thingsApi } from "./thingsApi"

const USE_VIRTUAL_LOCKER_ORDER_REQUEST_BTN = !!localStorage.getItem(
  "use_virtual_locker_order_request_btn",
)
export const thingsAdapter = createEntityAdapter<Thing, string>({
  selectId: (thing: Thing) => thing.id,
})

type ThingsState = EntityState<Thing, string> & {
  authorizedMessage: string
  selectedReservationId?: string
  selectedDoorId?: string
  selectedLockerId?: string
  clientSelectionIds: string[]
  filteredClientSelectionIds: string[]
  clientSelectionSearchValues: Record<string, string>
  clientSelectionDefaultValues: Record<string, string | undefined | number>
  mblsClients: MblsConnectorClient[]
  // mbls delivery reservation fields
  mblsReservationClientId: string
  mblsReservationDeliveryDate: string
  mblsReservationTimeConstraintStart: string
  mblsReservationTimeConstraintEnd: string
  mblsReservationSmartLockerNotificationPhone: string
  mblsReservationSmartLockerNotificationEmail: string
  mblsReservationOrderThirdPartyID: string
  mblsReservationNotes: string
  showMblsReservationSearchBarCloseBtn: boolean
  highlightedReservationId?: string
  userInputPhone: string | null
  userInputEmail: string | null
  userInputNotes: string | null
  saList: Record<string, any>[]
  postReservationCreationBannerMessage?: string
  // Door id for which a reservation is currently being created.
  // Used by the dashboard to render a loading state on that specific
  // door tile, preventing the brief "unlinked" flicker between SST
  // creating the reservation and SL2 returning the createThings response.
  creatingReservationDoorId?: string
  // Terminal tombstone set (LOCKER-321): reservation ids the client has
  // observed reach a terminal SST status. Every door-state write path
  // consults this so a stale/out-of-order snapshot (MQTT, getThings,
  // updateThings, ThingUpdated) can never resurrect a closed reservation
  // into CACHE-L (`sst2Locker.doors[].reservations[]`) or revert CACHE-R
  // (`sstReservation.status`) to a visible status. Generalizes #292.
  terminalReservationIds: Record<string, true>
}
// Define the initial state using that type
const initialState: ThingsState = {
  ...thingsAdapter.getInitialState(),
  authorizedMessage: "",
  clientSelectionIds: [],
  filteredClientSelectionIds: [],
  clientSelectionSearchValues: {},
  clientSelectionDefaultValues: {},
  mblsClients: [],
  mblsReservationClientId: "",
  mblsReservationDeliveryDate: "",
  mblsReservationTimeConstraintStart: "",
  mblsReservationTimeConstraintEnd: "",
  mblsReservationSmartLockerNotificationPhone: "",
  mblsReservationSmartLockerNotificationEmail: "",
  mblsReservationOrderThirdPartyID: "",
  mblsReservationNotes: "",
  showMblsReservationSearchBarCloseBtn: false,
  highlightedReservationId: "",
  selectedReservationId: "",
  selectedDoorId: "",
  selectedLockerId: "",
  userInputPhone: null,
  userInputEmail: null,
  userInputNotes: null,
  saList: [],
  postReservationCreationBannerMessage: undefined,
  creatingReservationDoorId: undefined,
  terminalReservationIds: {},
}

/**
 * Record every reservation that an incoming thing reports as TERMINAL into
 * the tombstone set — both the SL2 Reservation mirror's `sstReservation`
 * and a V2 locker's embedded door reservations. Keeps the set current from
 * polled/pushed snapshots so an even-staler later snapshot can't revive a
 * reservation this one already reported closed. (LOCKER-321)
 */
function markTerminalReservationIds(
  thing: any,
  tombstones: Record<string, true>,
): void {
  // V2-ONLY: only tombstone V2 reservations. V1 lockers use a separate slice
  // and a different render path that this mechanism must not touch (per
  // LOCKER-321 scope: V1 is left byte-identical to its pre-fix behavior).
  if (
    thing?.thingTypeMrn === ReservationThingTypeMrn &&
    thing?.sstReservationVersion === "v2"
  ) {
    const sst = thing.sstReservation
    if (sst && isSst2TerminalStatus(sst.status)) {
      const id = getSst2ReservationId(sst)
      if (id) tombstones[id] = true
    }
  }
  if (Array.isArray(thing?.sst2Locker?.doors)) {
    for (const id of collectTerminalReservationIds(thing.sst2Locker)) {
      tombstones[id] = true
    }
  }
}

/**
 * Merge an incoming thing onto an existing one while enforcing the
 * LOCKER-321 monotonic-write invariants:
 *   - CACHE-R: keep an existing terminal `sstReservation` snapshot against
 *     a stale non-terminal incoming one (generalized #292 stickiness).
 *   - CACHE-L: REPLACE `sst2Locker.doors` wholesale (never lodash
 *     index-merge) with the terminal-filtered incoming snapshot so a
 *     just-closed reservation cannot ghost back onto a door.
 */
function reconcileIncomingThing(
  incomingThing: any,
  existingThing: any,
  tombstones: Record<string, true>,
  // Whether to apply reservation terminal-stickiness to NON-v2 reservations.
  // `getThings` passes true so V1 keeps its pre-existing #292 stickiness
  // (with no V1 ids ever tombstoned this reduces to the exact #292 rule —
  // V1 behavior is unchanged). `updateThings`/`upsertOneDeep` pass false so
  // V1 reservations get their original plain-merge behavior (those paths had
  // NO stickiness before LOCKER-321 — adding it there would change V1).
  stickyNonV2Reservations = true,
): any {
  const mergedThing = merge({}, existingThing, incomingThing)

  const isReservation =
    existingThing?.thingTypeMrn === ReservationThingTypeMrn
  const reservationIsV2 =
    isReservation && existingThing?.sstReservationVersion === "v2"

  if (
    isReservation &&
    (reservationIsV2 || stickyNonV2Reservations) &&
    shouldKeepTerminalReservation(
      existingThing.sstReservation,
      mergedThing.sstReservation,
      tombstones,
    )
  ) {
    mergedThing.sstReservation = existingThing.sstReservation
  }

  if (Array.isArray(incomingThing?.sst2Locker?.doors)) {
    mergedThing.sst2Locker.doors = reconcileLockerDoors(
      incomingThing.sst2Locker.doors,
      tombstones,
    )
  }

  return mergedThing
}

/**
 * Terminal-filter a locker snapshot on a brand-new thing (no existing
 * entry to merge against) so the very first poll/push after MQTT already
 * closed a reservation can't introduce it. Non-locker things pass through.
 */
function reconcileNewThing(
  incomingThing: any,
  tombstones: Record<string, true>,
): any {
  if (!Array.isArray(incomingThing?.sst2Locker?.doors)) return incomingThing
  return {
    ...incomingThing,
    sst2Locker: {
      ...incomingThing.sst2Locker,
      doors: reconcileLockerDoors(incomingThing.sst2Locker.doors, tombstones),
    },
  }
}

export const thingsSlice = createSlice({
  name: "things",
  initialState,
  reducers: {
    addMany: thingsAdapter.addMany,
    addOne: thingsAdapter.addOne,
    removeOne: thingsAdapter.removeOne,
    updateOne: thingsAdapter.updateOne,
    upsertOne: thingsAdapter.upsertOne,
    upsertMany: thingsAdapter.upsertMany,
    removeMany: thingsAdapter.removeMany,
    removeAll: thingsAdapter.removeAll,
    updateMany: thingsAdapter.updateMany,
    setUnauthorizedMessage: (state, action) => {
      state.authorizedMessage = action.payload
    },
    upsertOneDeep: (state, action) => {
      const payload = action.payload
      // LOCKER-321: an MQTT ThingUpdated is a wholesale snapshot replace.
      // Feed the tombstone set, terminal-filter any locker door snapshot,
      // and keep an existing terminal reservation snapshot against a stale
      // non-terminal ThingUpdated so it can't revert a closed reservation.
      markTerminalReservationIds(payload, state.terminalReservationIds)
      const reconciled = reconcileNewThing(payload, state.terminalReservationIds)
      const existingThing = state.entities[payload.id]
      // V2-ONLY stickiness: this ThingUpdated path had no terminal-stickiness
      // before LOCKER-321, so gate it to v2 reservations to leave V1 exactly
      // as it was (plain wholesale replace below).
      if (
        existingThing &&
        (existingThing as any).thingTypeMrn === ReservationThingTypeMrn &&
        (existingThing as any).sstReservationVersion === "v2" &&
        shouldKeepTerminalReservation(
          (existingThing as any).sstReservation,
          (reconciled as any).sstReservation,
          state.terminalReservationIds,
        )
      ) {
        ;(reconciled as any).sstReservation = (existingThing as any).sstReservation
      }
      if (existingThing) {
        state.entities[payload.id] = JSON.parse(JSON.stringify(reconciled))
        return
      }
      thingsAdapter.addOne(state, reconciled)
    },
    /**
     * Apply an SST V2 reservation update to two places in Redux state
     * that both render door availability:
     *
     *   1. `locker.sst2Locker.doors[].reservations[]` — the V2 embedded
     *      reservation list. Drives the OsLockerDoorV2 tile's primary
     *      `hasActiveReservation` check.
     *   2. Each SL2 Reservation thing's embedded `sstReservation`
     *      snapshot — drives the dashboard's `unlinkedDoorIds`
     *      Direction-2 check (SL2 says reservation is active, SST says
     *      door is empty → "unlinked", gray tile).
     *
     * Without (2), a CLOSED V2 reservation correctly disappears from
     * (1), but the dashboard still sees an SL2 Reservation whose
     * embedded `sstReservation.status` is non-terminal (it was the
     * pre-CLOSED snapshot from a `useGetThingsQuery` fetch) and marks
     * the door unlinked. Symptom: drawer closes, door tile stays gray
     * until browser reload refetches the SL2 Reservations.
     *
     * Behavior on (1):
     *  - Found on a door + terminal status (CLOSED/CANCELLED/DELETED)
     *    → remove.
     *  - Found + non-terminal → replace in place.
     *  - Not found + non-terminal + `door_id` resolves to a known V2
     *    door → append.
     *  - Otherwise no-op.
     *
     * Behavior on (2): for every SL2 Reservation whose embedded
     * `sstReservation` matches this V2 reservation's id (either `_id`
     * — the SL2 wrapper's convention — or `id`), merge the payload's
     * status + timestamp fields into the embedded snapshot.
     *
     * V1 reservation payloads (which carry `_id` and nest `door`)
     * lack a top-level string `id` and so won't reach this reducer
     * (the router gates V2 dispatch on `typeof reservation.id ===
     * "string"`). The reducer is also a defensive no-op when no V2
     * locker AND no SL2 Reservation matches.
     */
    applyV2ReservationUpdate: (state, action: PayloadAction<any>) => {
      const payload = action.payload
      const reservationId: string | undefined =
        payload?.id ?? payload?.reservation_id
      if (!reservationId || typeof reservationId !== "string") return

      const isTerminal =
        typeof payload?.status === "string" &&
        (SST2_TERMINAL_STATUSES as readonly string[]).includes(payload.status)

      // ── Pass 1a: update the V2 Locker's embedded reservation. ──
      let v2HandledOnExistingDoor = false
      v2locker: for (const id of Object.keys(state.entities)) {
        const thing: any = state.entities[id]
        const doors = thing?.sst2Locker?.doors
        if (!Array.isArray(doors)) continue
        for (const door of doors) {
          const reservations = door?.reservations
          if (!Array.isArray(reservations)) continue
          const idx = reservations.findIndex(
            (r: any) => r?.id === reservationId,
          )
          if (idx === -1) continue
          if (isTerminal) {
            reservations.splice(idx, 1)
          } else {
            reservations[idx] = payload
          }
          v2HandledOnExistingDoor = true
          break v2locker
        }
      }

      // ── Pass 1b: append-on-known-door fallback (non-terminal only). ──
      // Skip a reservation the client already knows is terminal — a late,
      // out-of-order non-terminal MQTT must not resurrect it (LOCKER-321).
      if (
        !v2HandledOnExistingDoor &&
        !isTerminal &&
        !state.terminalReservationIds[reservationId]
      ) {
        const doorId: string | undefined =
          payload?.door_id ?? payload?.door?.id
        if (doorId && typeof doorId === "string") {
          for (const id of Object.keys(state.entities)) {
            const thing: any = state.entities[id]
            const doors = thing?.sst2Locker?.doors
            if (!Array.isArray(doors)) continue
            const door = doors.find((d: any) => d?.id === doorId)
            if (!door) continue
            if (!Array.isArray(door.reservations)) door.reservations = []
            door.reservations.push(payload)
            break
          }
        }
      }

      // ── Pass 2: update SL2 Reservation things' embedded sstReservation. ──
      // The dashboard's `unlinkedDoorIds` Direction-2 check reads
      // `r.sstReservation.status` and `r.sstReservation.door.door_id`. We
      // merge the new status + timestamps so `isSst2ReservationVisible`
      // flips false on terminal payloads and the door stops being
      // flagged unlinked.
      let matchedReservationMirror = false
      for (const id of Object.keys(state.entities)) {
        const thing: any = state.entities[id]
        if (thing?.thingTypeMrn !== ReservationThingTypeMrn) continue
        const sst = thing?.sstReservation
        if (!sst) continue
        if (sst._id !== reservationId && sst.id !== reservationId) continue
        matchedReservationMirror = true
        // Terminal-stickiness: never revert a known-terminal snapshot back
        // to a visible status from an out-of-order non-terminal MQTT.
        if (
          shouldKeepTerminalReservation(sst, payload, state.terminalReservationIds)
        ) {
          continue
        }
        sst.status = payload.status
        if ("delivery_time" in payload) sst.delivery_time = payload.delivery_time
        if ("reception_time" in payload) sst.reception_time = payload.reception_time
        if ("admin_time" in payload) sst.admin_time = payload.admin_time
      }

      // Tombstone the reservation once we've actually observed it reach a
      // terminal status on a door we track or a reservation mirror we hold.
      // Gated on "observed" so an unrelated terminal MQTT for a reservation
      // not in state leaves state untouched (no-op contract).
      if (isTerminal && (v2HandledOnExistingDoor || matchedReservationMirror)) {
        state.terminalReservationIds[reservationId] = true
      }
    },
    deleteByThingTypeMrn: (state, action: PayloadAction<string>) => {
      const entities = Object.values(state.entities)
      const things = entities.filter(
        (thing: any) => thing.thingTypeMrn === action.payload,
      )
      thingsAdapter.removeMany(
        state,
        things.map((t: any) => t.id),
      )
    },
    setClientSelectionIds: (state, action: PayloadAction<string[]>) => {
      assert(action.payload, Joi.array().items(Joi.string()))
      state.clientSelectionIds = action.payload
    },
    setFilteredClientSelectionIds: (state, action: PayloadAction<string[]>) => {
      assert(action.payload, Joi.array().items(Joi.string()))
      state.filteredClientSelectionIds = action.payload
    },
    setClientSelectionSearchValues: (
      state,
      action: PayloadAction<Record<string, string>>,
    ) => {
      assert(action.payload, Joi.object().pattern(Joi.string(), Joi.any()))
      state.clientSelectionSearchValues = action.payload
    },
    clearClientSelectionSearchValues: (state) => {
      state.clientSelectionSearchValues = {}
    },
    setClientSelectionDefaultValues: (
      state,
      action: PayloadAction<Record<string, string | undefined | number>>,
    ) => {
      assert(action.payload, Joi.object().pattern(Joi.string(), Joi.any()))
      state.clientSelectionDefaultValues = {
        ...action.payload,
      }
    },
    updateClientSelectionDefaultValues: (
      state,
      action: PayloadAction<Record<string, string | undefined | number>>,
    ) => {
      assert(action.payload, Joi.object().pattern(Joi.string(), Joi.any()))
      state.clientSelectionDefaultValues = {
        ...state.clientSelectionDefaultValues,
        ...action.payload,
      }
      // update selected email and phone if needed
      if (action.payload.email && isString(action.payload.email)) {
        state.userInputEmail = action.payload.email
      }
      if (action.payload.phone && isString(action.payload.phone)) {
        state.userInputPhone = action.payload.phone
      }
    },
    clearClientSelectionDefaultValues: (state) => {
      state.clientSelectionDefaultValues = {}
      state.userInputPhone = null
      state.userInputEmail = null
      state.userInputNotes = null
    },
    setUserInputPhone: (state, action: PayloadAction<string>) => {
      state.userInputPhone = action.payload
    },
    setUserInputEmail: (state, action: PayloadAction<string>) => {
      state.userInputEmail = action.payload
    },
    setUserInputNotes: (state, action: PayloadAction<string>) => {
      state.userInputNotes = action.payload
    },
    setMblsReservation: (
      state,
      action: PayloadAction<Record<string, string>>,
    ) => {
      assert(action.payload, Joi.object().pattern(Joi.string(), Joi.any()))
      state.mblsReservationClientId = action.payload.mblsReservationClientId
      state.mblsReservationDeliveryDate =
        action.payload.mblsReservationDeliveryDate
      state.mblsReservationTimeConstraintStart =
        action.payload.mblsReservationTimeConstraintStart
      state.mblsReservationTimeConstraintEnd =
        action.payload.mblsReservationTimeConstraintEnd
      state.mblsReservationSmartLockerNotificationPhone =
        action.payload.mblsReservationSmartLockerNotificationPhone
      state.mblsReservationSmartLockerNotificationEmail =
        action.payload.mblsReservationSmartLockerNotificationEmail
      state.mblsReservationOrderThirdPartyID =
        action.payload.mblsReservationOrderThirdPartyID
      state.mblsReservationNotes = action.payload.mblsReservationNotes
    },

    clearMblsReservation: (state) => {
      state.mblsReservationClientId = ""
      state.mblsReservationDeliveryDate = ""
      state.mblsReservationTimeConstraintStart = ""
      state.mblsReservationTimeConstraintEnd = ""
      state.mblsReservationSmartLockerNotificationPhone = ""
      state.mblsReservationSmartLockerNotificationEmail = ""
      state.mblsReservationOrderThirdPartyID = ""
      state.mblsReservationNotes = ""
    },
    setShowMblsReservationSearchBarCloseBtn: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showMblsReservationSearchBarCloseBtn = action.payload
    },
    setHighlightedReservationId: (state, action: PayloadAction<string>) => {
      state.highlightedReservationId = action.payload
    },
    setSelectedDoorId: (state, action: PayloadAction<string>) => {
      state.selectedDoorId = action.payload
    },
    clearSelectedDoorId: (state) => {
      state.selectedDoorId = ""
    },
    setSelectedReservationId: (state, action: PayloadAction<string>) => {
      state.selectedReservationId = action.payload
    },
    clearSelectedReservationId: (state) => {
      state.selectedReservationId = ""
    },
    clearSelectedDoorReservation: (state) => {
      state.selectedDoorId = ""
      state.selectedReservationId = ""
    },
    setSelectedLockerId: (state, action: PayloadAction<string>) => {
      state.selectedLockerId = action.payload
    },
    clearSelectedLockerId: (state) => {
      state.selectedLockerId = ""
    },
    setDoorReservationIds: (
      state,
      action: PayloadAction<{
        doorId: string
        reservationId: string
        lockerId: string
      }>,
    ) => {
      state.selectedDoorId = action.payload.doorId
      state.selectedReservationId = action.payload.reservationId
      state.selectedLockerId = action.payload.lockerId
    },
    clearSaList: (state) => {
      state.saList = []
    },
    removeItemFromSaList: (state, action: PayloadAction<string>) => {
      state.saList = state.saList.filter((item) => item.id !== action.payload)
    },
    setPostReservationCreationBannerMessage: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.postReservationCreationBannerMessage = action.payload
    },
    setCreatingReservationDoorId: (state, action: PayloadAction<string>) => {
      state.creatingReservationDoorId = action.payload
    },
    clearCreatingReservationDoorId: (state) => {
      state.creatingReservationDoorId = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      thingsApi.endpoints.getThings.matchFulfilled,
      (state, arg) => {
        const { payload } = arg
        const params = get(arg, "meta.arg.originalArgs.params", {}) as any
        if (params["ap.name"]?.includes("SaGet")) {
          // append, make sure to not duplicate
          const list = [...state.saList, ...payload.things]
          const uniqueList = list.filter(
            (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
          )

          state.saList = uniqueList
          return
        }
        const fetchedThings = get(payload, "things", []) as Thing[]
        fetchedThings.forEach((thing) => {
          // Keep the terminal tombstone current from this poll so a later,
          // even-staler poll can't resurrect a reservation this one already
          // reported closed. (LOCKER-321)
          markTerminalReservationIds(thing, state.terminalReservationIds)

          const existingThing = state.entities[thing.id]
          if (existingThing) {
            // Monotonic merge: CACHE-R terminal-stickiness (generalized
            // #292) + CACHE-L wholesale door replace with terminal filter.
            // Without the CACHE-L half, lodash `merge()` index-merges
            // `sst2Locker.doors[].reservations[]` — it cannot delete array
            // elements, so a stale in-flight snapshot ghosts a just-closed
            // reservation back onto the door and the tile reverts to
            // OCCUPIED / gray "Non disponible" until reload.
            const mergedThing = reconcileIncomingThing(
              thing,
              existingThing,
              state.terminalReservationIds,
            )
            thingsAdapter.updateOne(state, {
              id: thing.id,
              changes: mergedThing,
            })
          } else {
            // New thing — still terminal-filter a locker snapshot so a poll
            // arriving after MQTT closed a reservation can't introduce it.
            thingsAdapter.addOne(
              state,
              reconcileNewThing(thing, state.terminalReservationIds),
            )
          }
        })
      },
    )
    // update existing things for updateThings data.things
    builder.addMatcher(
      thingsApi.endpoints.updateThings.matchFulfilled,
      (state, { payload }) => {
        const updatedThings = get(payload, "data.things", []) as Thing[]
        updatedThings.forEach((updatedThing) => {
          // Same monotonic-write guards as getThings (LOCKER-321): a
          // mutation response is another async writer into the door-state
          // caches and must not revert a known-terminal reservation.
          // NOTE stickyNonV2Reservations=false below: this path had no
          // stickiness pre-LOCKER-321, so V1 reservations keep their exact
          // original plain-merge behavior; only v2 reservations get the guard.
          markTerminalReservationIds(updatedThing, state.terminalReservationIds)

          const existingThing = state.entities[updatedThing.id]
          if (existingThing) {
            const mergedThing = reconcileIncomingThing(
              updatedThing,
              existingThing,
              state.terminalReservationIds,
              false,
            )
            thingsAdapter.upsertOne(state, mergedThing)
          } else {
            thingsAdapter.addOne(
              state,
              reconcileNewThing(updatedThing, state.terminalReservationIds),
            )
          }
        })

        // update things in saList
        const saList = state.saList.map((t: any) => {
          const updatedThing = updatedThings.find((ut) => ut.id === t.id)
          return updatedThing ? merge({}, t, updatedThing) : t
        })
        state.saList = saList
      },
    )
    // matcher to delete things. extract response.data.ids and remove them from state
    builder.addMatcher(
      thingsApi.endpoints.deleteThings.matchFulfilled,
      (state, { payload }) => {
        const deletedThingIds = get(payload, "data.ids", []) as string[]
        thingsAdapter.removeMany(state, deletedThingIds)
        // check if deleted thing is in saList
        const saList = state.saList.filter(
          (t: any) => !deletedThingIds.includes(t.id),
        )
        state.saList = saList
      },
    )
    builder.addMatcher(
      thingsApi.endpoints.getThings.matchRejected,
      (state, { meta }) => {
        if (get(meta, "baseQueryMeta.response.status") === 403) {
          console.log("Unauthorized request.", meta)
          state.authorizedMessage = "Unauthorized request"
        }
      },
    )
    builder.addMatcher(
      thingsApi.endpoints.createThings.matchRejected,
      (state, { meta }) => {
        if (get(meta, "baseQueryMeta.response.status") === 403) {
          console.log("Unauthorized request.", meta)
          state.authorizedMessage = "Unauthorized request"
        }
      },
    )
    builder.addMatcher(
      thingsApi.endpoints.updateThings.matchRejected,
      (state, { meta }) => {
        if (get(meta, "baseQueryMeta.response.status") === 403) {
          console.log("Unauthorized request.", meta)
          state.authorizedMessage = "Unauthorized request"
        }
      },
    )
    builder.addMatcher(
      thingsApi.endpoints.deleteThings.matchRejected,
      (state, { meta }) => {
        if (get(meta, "baseQueryMeta.response.status") === 403) {
          console.log("Unauthorized request.", meta)
          state.authorizedMessage = "Unauthorized request"
        }
      },
    )
    builder.addMatcher(
      mblsconnectorApi.endpoints.getMblsConnectorClients.matchFulfilled,
      (state, { payload }) => {
        state.mblsClients = payload.data
      },
    )
  },
})

export const thingsActions = thingsSlice.actions
export const {
  selectAll: selectAllThings,
  selectById: selectThingById,
  selectIds: selectThingIds,
  // Add other selectors here
} = thingsAdapter.getSelectors((state: RootState) => state.things)

export default thingsSlice.reducer

export const selectThingsByThingTypeMrnFilter = createSelector(
  // First, define the input selectors. These should be simple functions that return a part of the state
  [
    (state: RootState) => selectAllThings(state),
    (_state, thingTypeMrn) => thingTypeMrn,
    (_state, _thingTypeMrn, filterFn: (thing: any) => boolean) => filterFn,
  ],

  // Then, define the result function. This function will receive the outputs of the input selectors in the same order
  (things, thingTypeMrn, filterFn) =>
    things.filter(
      (thing) => thing.thingTypeMrn === thingTypeMrn && filterFn(thing),
    ),
)

export const selectThingsByThingTypeMrn = createSelector(
  // First, define the input selectors. These should be simple functions that return a part of the state
  [
    (state: RootState) => selectAllThings(state),
    (_, thingTypeMrn) => thingTypeMrn,
  ],

  // Then, define the result function. This function will receive the outputs of the input selectors in the same order
  (things, thingTypeMrn) =>
    things.filter((thing) => thing.thingTypeMrn === thingTypeMrn),
)

export const selectUnauthorizedMessage = (state: RootState) =>
  state.things.authorizedMessage

export const selectSelectedReservation = (state: RootState) =>
  state.things.entities[state.things.selectedReservationId || ""]

export const selectClientSelectionList = (state: RootState) => {
  const clients = selectThingsByThingTypeMrn(
    state,
    ClientThingTypeMrn,
  ) as unknown as Client[]
  const { clientSelectionSearchValues = {} } = state.things
  const { firstName, lastName, email, phone } = clientSelectionSearchValues

  return clients.filter((c: Client) => {
    const conditions = []
    if (firstName) {
      conditions.push(
        c.firstName?.toLowerCase().includes(firstName.toLowerCase()),
      )
    }
    if (lastName) {
      conditions.push(
        c.lastName?.toLowerCase().includes(lastName.toLowerCase()),
      )
    }
    if (email) {
      conditions.push(c.email?.toLowerCase().includes(email.toLowerCase()))
    }
    if (phone) {
      conditions.push(c.phone?.toLowerCase().includes(phone.toLowerCase()))
    }

    return conditions.some((c) => c) || conditions.length === 0
  })
}

export const selectFilteredClientSelectionList = (state: RootState) =>
  state.things.filteredClientSelectionIds.map(
    (id: string) => state.things.entities[id],
  )

export const selectClientSelectionSearchValues = (state: RootState) =>
  state.things.clientSelectionSearchValues

export const selectClientSelectionDefaultValues = (state: RootState) =>
  state.things.clientSelectionDefaultValues

export const selectMblsClients = (state: RootState) => state.things.mblsClients

export const selectMblsReservation = (state: RootState) => ({
  clientId: state.things.mblsReservationClientId,
  deliveryDate: state.things.mblsReservationDeliveryDate,
  timeConstraintStart: state.things.mblsReservationTimeConstraintStart,
  timeConstraintEnd: state.things.mblsReservationTimeConstraintEnd,
  smartLockerNotificationPhone:
    state.things.mblsReservationSmartLockerNotificationPhone,
  smartLockerNotificationEmail:
    state.things.mblsReservationSmartLockerNotificationEmail,
  orderThirdPartyID: state.things.mblsReservationOrderThirdPartyID,
  notes: state.things.mblsReservationNotes,
})

export const selectMblsReservationClient = (state: RootState) =>
  // get mblsCient with id from mblsReservationClientId
  state.things.mblsClients.find(
    (c: any) => c.id === state.things.mblsReservationClientId,
  )

export const selectMblsReservationClientId = (state: RootState) =>
  state.things.mblsReservationClientId

export const selectIsLoadingMblsReservationClient = (state: RootState) =>
  state.things.mblsClients.length === 0

export const selectShowMblsReservationSearchBarCloseBtn = (state: RootState) =>
  state.things.showMblsReservationSearchBarCloseBtn

export const selectHighlightedReservation = (state: RootState) =>
  state.things.entities[state.things.highlightedReservationId || ""]

export const selectHighlightedReservationId = (state: RootState) =>
  state.things.highlightedReservationId

export const selectCreatingReservationDoorId = (state: RootState) =>
  state.things.creatingReservationDoorId

export const selectSaList = <T>(state: RootState): T[] =>
  state.things.saList as T[]

export const selectSaThingById = (state: RootState, id: string) =>
  state.things.saList.find((t: any) => t.id === id)

// selected door + reservation + session
export type DoorReservation = {
  locker?: Locker
  door?: SstDoor
  reservation?: Reservation
  session?: LockerDoorReservationSession
  // EVERY session held on the selected door, not only the first match.
  // LOCKER-377: once duplicate rows existed, the old `find` made the twin
  // invisible, so the drawer's close released one row and the other kept the
  // door "reserving" until fnCheckReservationSessions reaped it.
  sessions?: LockerDoorReservationSession[]
}

// MEMOIZED (LOCKER-377). This was a plain function returning a fresh object
// literal, so with `useAppSelector`'s default reference equality every consumer
// re-rendered on EVERY dispatched action in the app. That included
// `OsLockerDoorDrawerManager`, whose async create effect then re-ran inside its
// own network window — which is what turned the duplicate-session race from
// theoretical into the rows seen in production.
export const selectSelectedDoorReservation = createSelector(
  [
    (state: RootState) => selectAllThings(state),
    (state: RootState) => state.things.selectedLockerId,
    (state: RootState) => state.things.selectedDoorId,
    (state: RootState) => state.things.selectedReservationId,
  ],
  (things, selectedLockerId, selectedDoorId, selectedReservationId) => {
    // selectedLockerId
    const locker = (
      selectedLockerId
        ? things.find((t) => t.id === selectedLockerId)
        : undefined
    ) as Locker

    // Find door from V1 sstLocker or V2 sst2Locker
    let door: SstDoor | undefined
    if (locker?.sstApiVersion === "v2" && locker?.sst2Locker?.doors) {
      const v2Door = locker.sst2Locker.doors.find(
        (d: any) => d.id === selectedDoorId,
      )
      if (v2Door) {
        // Map V2 door to V1 SstDoor shape for drawer compatibility.
        // Size lives on the cell — pick the door's template via locker_group
        // (cell_id is unique only within a template), then find the cell by id.
        const tmplAssoc =
          locker.sst2Locker?.templates_association?.[v2Door.locker_group]
        const cell = tmplAssoc?.template?.cells?.find(
          (c: any) => c.id === v2Door.cell_id,
        )
        door = {
          door_id: v2Door.id,
          door_number: v2Door.door_number,
          door_label: v2Door.door_label,
          size: cell?.size ?? "",
          size_description: "",
          locker_id: locker.sstLockerId || "",
        } as SstDoor
      }
    } else {
      door = locker?.sstLocker?.doors?.find(
        (d: SstDoor) => d.door_id === selectedDoorId,
      )
    }

    let reservation = (
      selectedReservationId
        ? things.find((t) => t.id === selectedReservationId)
        : undefined
    ) as Thing

    // V2 fallback: if no reservation found by ID, search by matching SST reservation ID
    // This handles cases where the dashboard couldn't match (timing, external reservations, etc.)
    if (!reservation?.id && locker?.sstApiVersion === "v2" && door?.door_id) {
      const v2Door = locker.sst2Locker?.doors?.find(
        (d: any) => d.id === door.door_id,
      )
      const activeV2Res = (v2Door?.reservations || []).find(
        isSst2ReservationVisible,
      )
      if (activeV2Res) {
        const matched = things.find(
          (t) =>
            t.thingTypeMrn === ReservationThingTypeMrn &&
            t.lockerId === locker.id &&
            (t as any).sstReservation?._id === activeV2Res.id,
        )
        if (matched) {
          reservation = matched as Thing
        }
      }
    }

    const allSessions = things.filter(
      (t) => t.thingTypeMrn === LockerDoorReservationSessionThingTypeMrn,
    ) as unknown as LockerDoorReservationSession[]

    // Match predicate deliberately UNCHANGED: `sstDoorId` only, with no
    // `lockerId` filter. Whether SST door ids are globally unique or unique
    // only per locker was never verified, and LOCKER-377 scopes that question
    // out — so this widens nothing and narrows nothing. `sessions` holds every
    // match so a close can release all of them; `session` stays the first, so
    // existing single-session UI is untouched.
    const sessions = door?.door_id
      ? allSessions.filter(
          (s: LockerDoorReservationSession) => s.sstDoorId === door?.door_id,
        )
      : []

    const session = sessions[0]
    return {
      locker,
      door,
      reservation,
      session,
      sessions,
    } as unknown as DoorReservation
  },
)

export type SelectedClient = {
  id: string
  firstName: string
  lastName: string
  email: string
  language?: string
  phones?: { id: string; number: string; label: string }[]
  addresses?: {
    id: string
    street: string
    city: string
    postalCode: string
    country: string
    label: string
  }[]
}

export const selectClientById = (state: RootState, clientId: string) => {
  // if mblsReservationClientId exist, return mblsClient with that id. othewise attempt to return clients with defaultValues.clientId
  const mblsClient0 = state.things.mblsClients.find(
    (c: any) => c.id === clientId,
  )
  if (mblsClient0) {
    let phones = isArray(mblsClient0?.phones) ? mblsClient0?.phones : []

    if (state.things.mblsReservationSmartLockerNotificationPhone) {
      phones = [
        {
          id: "new",
          number: state.things.mblsReservationSmartLockerNotificationPhone,
          label: "custom",
        },
        ...phones,
      ]
    }

    let email = mblsClient0?.email

    if (state.things.mblsReservationSmartLockerNotificationEmail) {
      email = state.things.mblsReservationSmartLockerNotificationEmail
    }

    return {
      id: mblsClient0?.id,
      firstName: mblsClient0?.firstName,
      lastName: mblsClient0?.lastName,
      email,
      phones,
      addresses: isArray(mblsClient0?.addresses) ? mblsClient0?.addresses : [],
      language: mblsClient0?.language,
    } as unknown as SelectedClient
  }

  const mblsClient1 = state.things.mblsClients.find(
    (c: any) => c.id === clientId,
  )

  if (mblsClient1) {
    return {
      id: mblsClient1?.id,
      firstName: mblsClient1?.firstName,
      lastName: mblsClient1?.lastName,
      email: mblsClient1?.email,
      language: mblsClient1?.language,
      phones: mblsClient1?.phones,
      addresses: mblsClient1?.addresses,
    } as unknown as SelectedClient
  }

  const client = state.things.entities[clientId]

  if (!client) return null

  return {
    id: client?.id,
    firstName: client?.firstName,
    lastName: client?.lastName,
    email: client?.email,
    phones: [
      {
        id: client?.phone,
        number: client?.phone,
        label: "default",
      },
    ],
    addresses: [],
  } as unknown as SelectedClient
}

export const selectSelectedClient = (state: RootState) => {
  const clientId =
    state.things.mblsReservationClientId ||
    state.things.clientSelectionDefaultValues?.clientId
  return selectClientById(state, clientId + "")
}

export const selectUserInputPhone = (state: RootState) =>
  state.things.userInputPhone

export const selectUserInputEmail = (state: RootState) =>
  state.things.userInputEmail

export const selectUserInputNotes = (state: RootState) =>
  state.things.userInputNotes

export const selectReservationFormDefaultValues = (state: RootState) => {
  let firstName = state.things.clientSelectionDefaultValues?.firstName || ""
  let lastName = state.things.clientSelectionDefaultValues?.lastName || ""
  let email = state.things.clientSelectionDefaultValues?.email || ""
  let phone = state.things.clientSelectionDefaultValues?.phone || ""

  let clientId = state.things.clientSelectionDefaultValues?.clientId || ""

  let notes =
    state.things.userInputNotes ||
    state.things.mblsReservationNotes ||
    state.things.clientSelectionDefaultValues?.notes ||
    ""

  const selectedClient = selectSelectedClient(state)
  if (selectedClient?.id) {
    firstName = selectedClient?.firstName
    lastName = selectedClient?.lastName
    email = selectedClient?.email || ""
    phone = selectedClient?.phones?.[0]?.number || ""
    clientId = selectedClient?.id || ""
  }

  if (
    state.things.mblsReservationSmartLockerNotificationPhone &&
    !state.things.mblsReservationSmartLockerNotificationEmail
  ) {
    phone = state.things.mblsReservationSmartLockerNotificationPhone
    email = ""
  } else if (
    !state.things.mblsReservationSmartLockerNotificationPhone &&
    state.things.mblsReservationSmartLockerNotificationEmail
  ) {
    phone = ""
    email = state.things.mblsReservationSmartLockerNotificationEmail
  } else if (
    state.things.mblsReservationSmartLockerNotificationPhone &&
    state.things.mblsReservationSmartLockerNotificationEmail
  ) {
    phone = state.things.mblsReservationSmartLockerNotificationPhone
    email = state.things.mblsReservationSmartLockerNotificationEmail
  }

  return {
    ...state.things.clientSelectionDefaultValues,
    firstName,
    lastName,
    email,
    phone,
    clientId,
    notes,
  }
}

export type LockerMetrics = {
  nbOfReservations: number
  nbOfDoors: number
  occupancy: number
  isVirtual?: boolean
}

export const selectLockerMetrics = createSelector(
  [(state: RootState) => selectAllThings(state)],
  (things) => {
    const lockers = things.filter(
      (thing) => thing.thingTypeMrn === LockerThingTypeMrn,
    )
    const reservationList = things.filter(
      (thing) => thing.thingTypeMrn === ReservationThingTypeMrn,
    )

    const metrics = lockers.reduce(
      (acc, locker) => {
        const lockerReservations = reservationList.filter(
          (reservation) => reservation?.lockerId === locker.id,
        )
        const isV2Locker = locker?.sstApiVersion === "v2"
        const nbOfDoors = isV2Locker
          ? locker?.sst2Locker?.doors?.length || 0
          : locker?.sstLocker?.doors?.length || 0

        let nbOfReservations: number
        if (isV2Locker) {
          // V2: count doors that have active embedded reservations
          nbOfReservations = (locker?.sst2Locker?.doors || []).filter(
            (door: any) =>
              (door.reservations || []).some(isSst2ReservationVisible),
          ).length
        } else {
          // V1: count doors linked to active SL2 Reservations via SST link
          nbOfReservations = (locker?.sstLocker?.doors || []).filter(
            (door: SstDoor) => {
              return lockerReservations.some(
                (r) =>
                  r?.sstReservation?._id ===
                    (door.links &&
                      door?.links[0]?.href?.split("reservations/")?.pop()) &&
                  ["CONFIRMED", "EXPIRED", "DELIVERED"].includes(
                    r?.sstReservation?.status || "",
                  ),
              )
            },
          ).length
        }

        const occupancy = nbOfDoors > 0 ? nbOfReservations / nbOfDoors : 0

        acc[locker.id] = {
          nbOfReservations,
          nbOfDoors,
          // 2 decimal number
          occupancy: Math.round(occupancy * 100) / 100,
          isVirtual: locker.isVirtual,
        }
        return acc
      },
      {} as Record<
        string,
        {
          nbOfReservations: number
          nbOfDoors: number
          occupancy: number
          isVirtual?: boolean
        }
      >,
    )
    // calculate total metrics for non-virtual lockers
    const totalMetrics = Object.values(metrics).reduce(
      (total, { nbOfReservations, nbOfDoors, isVirtual }) => {
        if (isVirtual && !USE_VIRTUAL_LOCKER_ORDER_REQUEST_BTN) return total
        total.nbOfReservations += nbOfReservations
        total.nbOfDoors += nbOfDoors
        return total
      },
      { nbOfReservations: 0, nbOfDoors: 0, occupancy: 0 },
    )

    totalMetrics.occupancy =
      totalMetrics.nbOfDoors > 0
        ? Math.round(
            (totalMetrics.nbOfReservations / totalMetrics.nbOfDoors) * 100,
          ) / 100
        : 0

    return { ...metrics, total: totalMetrics } as Record<string, LockerMetrics>
  },
)
export const selectPostReservationCreationBannerMessage = (state: RootState) =>
  state.things.postReservationCreationBannerMessage
